// src/pages/VerifyAccount.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; // ✅ Added useLocation
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { authenticatedFetch } from '../utils/api';
import SubFooter from '../frontend/components/SubFooter';

const API_BASE = (process.env.REACT_APP_API_URL || 'https://fslapi.devformula.com/api').trim();

function VerifyAccount() {
  const navigate = useNavigate();
  const location = useLocation(); // ✅ Get location for state
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(294);
  const [isResending, setIsResending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState('');
  const inputRefs = useRef([]);

  // ✅ Get email from multiple sources: location.state > localStorage > fallback
  const getEmail = () => {
    // Priority 1: From Login redirect (location.state)
    if (location.state?.email) {
      return location.state.email;
    }
    // Priority 2: From signup flow (localStorage)
    const step1 = JSON.parse(localStorage.getItem('signupStep1') || '{}');
    if (step1?.email) return step1.email;
    
    const signupComplete = JSON.parse(localStorage.getItem('signupComplete') || '{}');
    if (signupComplete?.email) return signupComplete.email;
    
    // Fallback
    return '';
  };
  
  const [userEmail, setUserEmail] = useState(getEmail());

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    
    console.log('🔐 VerifyAccount mounted');
    console.log('📦 location.state:', location.state);
    console.log('📦 signupStep1:', localStorage.getItem('signupStep1'));
    console.log('📦 signupComplete:', localStorage.getItem('signupComplete'));
    console.log('📦 otpReference:', localStorage.getItem('otpReference'));
    
    // ✅ Get email from any source
    const email = getEmail();
    
    // ✅ Allow access if we have email + (signupComplete OR otpReference OR fromLogin flag)
    const fromLogin = location.state?.fromLogin;
    const signupComplete = localStorage.getItem('signupComplete');
    const otpReference = localStorage.getItem('otpReference');
    
    // ✅ More flexible: allow access from Login flow even without signupComplete
    if (!email) {
      console.warn('⚠️ No email found, redirecting to /get-started');
      toast.warning('Session expired, please restart signup');
      setTimeout(() => {
        navigate('/get-started', { replace: true });
      }, 1000);
      return;
    }
    
    // ✅ If from Login flow, we don't require signupComplete/otpReference
    if (!fromLogin && !signupComplete && !otpReference) {
      console.warn('⚠️ Missing required signup data, redirecting to /get-started');
      toast.warning('Session expired, please restart signup');
      setTimeout(() => {
        navigate('/get-started', { replace: true });
      }, 1000);
      return;
    }
    
    // ✅ Update state with email if not already set
    if (email && !userEmail) {
      setUserEmail(email);
    }
    
  }, [navigate, location.state, userEmail]);

  // Countdown timer effect
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleChange = (index, value) => {
    if (value.length > 1) value = value[0];
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (apiErrorMessage) setApiErrorMessage('');
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = pastedData.split('');
    while (newCode.length < 6) newCode.push('');
    setCode(newCode);
    
    const nextEmptyIndex = newCode.findIndex((digit) => digit === '');
    const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5;
    inputRefs.current[focusIndex]?.focus();
    
    if (apiErrorMessage) setApiErrorMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiErrorMessage('');
    
    const verificationCode = code.join('');
    
    if (verificationCode.length !== 6) {
      const msg = 'Please enter the complete 6-digit code';
      setApiErrorMessage(msg);
      toast.warning(msg);
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        email: userEmail.toLowerCase().trim(),
        otp: parseInt(verificationCode, 10)
      };

      console.log('📤 Verifying OTP:', payload);
      
      const result = await authenticatedFetch(
        `${API_BASE}/HMO/verify-otp`,
        'POST',
        payload
      );

      console.log('✅ API Response:', result);

      if (result.success === false || result.status === 'error') {
        const errorMessage = result.message || 'Verification failed. Please try again.';
        setApiErrorMessage(errorMessage);
        toast.error(errorMessage);
        return;
      }

      const successMsg = result.message || 'OTP verified successfully!';
      toast.success(successMsg);
      
      // Store verified session data
      const step1Data = JSON.parse(localStorage.getItem('signupStep1') || '{}');
      const sessionData = {
        ...step1Data,
        email: userEmail, // ✅ Ensure email is saved
        verificationCode,
        sessionId: result.data?.sessionId || result.sessionId,
        sessionExpiresAt: Date.now() + ((result.data?.expiresInSeconds || result.expiresInSeconds || 3600) * 1000),
        isVerified: true,
        verifiedAt: new Date().toISOString()
      };
      
      localStorage.setItem('signupStep3', JSON.stringify(sessionData));
      console.log('💾 Stored signupStep3:', sessionData);
      
      setTimeout(() => {
        navigate('/verification-complete', { 
          replace: true,
          state: { 
            sessionId: result.data?.sessionId || result.sessionId,
            message: successMsg,
            email: userEmail
          } 
        });
      }, 500);

    } catch (error) {
      console.error('💥 OTP Verification Error:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      
      let errorMsg = 'Failed to verify OTP. Please try again.';
      
      if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
        errorMsg = 'Cannot connect to server. Please check your internet connection.';
      } else if (error.message.includes('Invalid OTP') || error.message.includes('expired')) {
        errorMsg = 'Invalid or expired code. Please request a new one.';
      }
      
      setApiErrorMessage(errorMsg);
      toast.error(errorMsg);
      
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (isResending || timeLeft > 240 || isLoading) return;
    
    setIsResending(true);
    setApiErrorMessage('');
    
    try {
      const payload = {
        email: userEmail.toLowerCase().trim()
      };
      
      console.log('🔄 Resending OTP:', payload);
      
      const result = await authenticatedFetch(
        `${API_BASE}/HMO/resend-otp`,
        'POST',
        payload
      );
      
      if (result?.success === false || result?.status === 'error') {
        throw new Error(result.message || 'Failed to resend code');
      }
      
      toast.success('New verification code sent! Check your email.');
      setTimeLeft(294);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      
    } catch (error) {
      console.error('❌ Resend error:', error);
      
      let errorMsg = error.message || 'Failed to resend code. Please try again.';
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        errorMsg = 'Authentication failed. Please restart the signup process.';
      } else if (error.message.includes('404') || error.message.includes('not found')) {
        errorMsg = 'Account not found. Please verify your email address.';
      } else if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
        errorMsg = 'Cannot connect to server. Please check your internet connection.';
      }
      
      toast.error(errorMsg);
      setApiErrorMessage(errorMsg);
    } finally {
      setIsResending(false);
    }
  };

  const handleGoBack = () => {
    // ✅ If from Login, go back to Login; otherwise go to CreatePassword
    if (location.state?.fromLogin) {
      navigate('/login', { 
        state: { 
          prefillEmail: userEmail 
        } 
      });
    } else {
      navigate('/create-password');
    }
  };

  const isCodeComplete = code.every((digit) => digit !== '' && /^\d$/.test(digit));

  return (
    <div className="verify-account-page">
      <ToastContainer position="top-right" autoClose={5000} theme="light" />

      {/* Header */}
      <header className="header-area header-one">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center py-3">
            <a href="/" className="brand-logo">
              <img src="images/fsl_logo.png" alt="FSL HMO Logo" style={{ height: '50px' }} />
            </a>
            <button 
              className="theme-btn style-two" 
              onClick={() => navigate('/')}
              disabled={isLoading}
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </header>

      {/* Form Section */}
      <section className="verify-account-section py-5" style={{ background: '#fafafa', minHeight: '80vh' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8">
              {/* Progress Indicators */}
              <div className="text-center mb-4" data-aos="fade-down">
                <div className="d-inline-flex align-items-center gap-2">
                  <span className="d-inline-block rounded-circle bg-success" style={{ width: '12px', height: '12px' }}></span>
                  <span className="d-inline-block rounded-circle bg-success" style={{ width: '12px', height: '12px' }}></span>
                  <span className="d-inline-block rounded-circle bg-success" style={{ width: '12px', height: '12px' }}></span>
                  <span className="d-inline-block rounded-circle bg-light border" style={{ width: '12px', height: '12px' }}></span>
                </div>
              </div>

              {/* Title */}
              <h2 className="text-center mb-4" data-aos="fade-up">Verify your account</h2>

              {/* Info Box */}
              <div className="alert alert-info border-0 mb-4" role="alert" data-aos="fade-up" style={{ backgroundColor: '#f3f0ff', color: '#5a4fcf', border: '1px solid #d4ceff' }}>
                <div className="d-flex align-items-start">
                  <i className="fas fa-info-circle me-2 mt-1" style={{ color: '#5a4fcf' }}></i>
                  <div>
                    <small className="d-block">We sent a code to <strong>{userEmail || 'your email'}</strong></small>
                    <small>Enter the 6-digit code below</small>
                  </div>
                </div>
              </div>

              {/* Form Card */}
              <div className="card shadow-sm border-0" data-aos="fade-up" data-aos-delay="200">
                <div className="card-body p-4 p-md-5">
                  <form onSubmit={handleSubmit} noValidate>
                    {/* ✅ Inline error message display */}
                    {apiErrorMessage && (
                      <div className="alert alert-danger border-0 mb-3" role="alert">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {apiErrorMessage}
                      </div>
                    )}

                    {/* Verification Code Inputs */}
                    <div className="mb-4">
                      <div className="d-flex justify-content-center gap-2 mb-4">
                        {code.map((digit, index) => (
                          <input
                            key={index}
                            ref={(el) => (inputRefs.current[index] = el)}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={index === 0 ? handlePaste : undefined}
                            className="form-control form-control-lg text-center"
                            style={{
                              width: '60px',
                              height: '60px',
                              fontSize: '24px',
                              fontWeight: 'bold',
                              backgroundColor: '#f8f9fa',
                              border: apiErrorMessage ? '2px solid #dc3545' : '1px solid #e9ecef',
                              borderRadius: '8px'
                            }}
                            required
                            disabled={isLoading}
                            autoComplete="one-time-code"
                          />
                        ))}
                      </div>
                    </div>

                    {/* Timer */}
                    <div className="text-center mb-4">
                      <div className="d-flex align-items-center justify-content-center gap-2 text-muted small">
                        <i className="far fa-clock"></i>
                        <span>Expires in</span>
                        <span className={`fw-semibold ${timeLeft < 60 ? 'text-danger' : 'text-success'}`}>
                          {formatTime(timeLeft)}
                        </span>
                      </div>
                    </div>

                    {/* Buttons */}
                    <div className="d-flex justify-content-between align-items-center gap-3 mt-5">
                      <button
                        type="button"
                        className="btn btn-link text-muted text-decoration-none"
                        onClick={handleGoBack}
                        disabled={isLoading}
                      >
                        ← Go back
                      </button>
                      
                      <div className="d-flex align-items-center gap-3">
                        <small className="text-muted">
                          Didn't get any code?{' '}
                          <button
                            type="button"
                            className="btn btn-link text-decoration-none p-0"
                            onClick={handleResend}
                            disabled={isResending || timeLeft > 240 || isLoading}
                            style={{ 
                              color: (isResending || timeLeft > 240 || isLoading) ? '#6c757d' : '#5a4fcf',
                              fontWeight: '600'
                            }}
                          >
                            {isResending ? 'Resending...' : 'Resend'}
                          </button>
                        </small>
                        
                        <button
                          type="submit"
                          className={`theme-btn style-one btn-lg px-4 py-3 ${!isCodeComplete || isLoading ? 'disabled' : ''}`}
                          disabled={!isCodeComplete || isLoading}
                          style={{ 
                            fontWeight: '600',
                            minWidth: '140px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {isLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                              Verifying...
                            </>
                          ) : (
                            <>
                              Continue <i className="fas fa-arrow-right ms-2"></i>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              {/* Security Notice */}
              <div className="text-center mt-4">
                <small className="text-muted">
                  <i className="fas fa-shield-alt me-1"></i>
                  Your verification code is secure and expires in 5 minutes
                </small>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Footer */}
      <SubFooter/>
    </div>
  );
}

export default VerifyAccount;